import { supabase } from '@/lib/supabase';

export interface DataSubjectRequest {
  id?: string;
  user_id: string;
  request_type: 'data_access' | 'data_rectification' | 'data_erasure' | 'data_portability' | 'processing_restriction' | 'consent_withdrawal' | 'objection_processing' | 'automated_decision_objection';
  description?: string;
  status: 'pending' | 'processing' | 'completed' | 'rejected';
  urgency_level: 'low' | 'medium' | 'high' | 'critical';
  created_at?: string;
  updated_at?: string;
  resolved_at?: string;
  resolution_notes?: string;
  dpo_notified_at?: string;
}

export interface UserDataExport {
  user_profile: any;
  candidates: any[];
  jobs: any[];
  analysis_results: any[];
  consents: any[];
  interviews: any[];
  projects: any[];
  user_actions: any[];
  export_metadata: {
    export_date: string;
    export_format: string;
    data_controller: string;
    export_scope: string;
    total_records: number;
  };
}

export interface ConsentRecord {
  id?: string;
  user_id: string;
  consent_type: 'analytics' | 'marketing' | 'functional' | 'essential' | 'data_processing' | 'ai_analysis';
  consent_given: boolean;
  processing_purpose: string;
  consent_method: 'explicit' | 'implicit' | 'opt_in' | 'pre_checked';
  legal_basis: 'consent' | 'legitimate_interest' | 'contract' | 'legal_obligation';
  consent_date: string;
  consent_withdrawn?: boolean;
  withdrawal_date?: string;
  withdrawal_method?: string;
  created_at?: string;
  updated_at?: string;
}

export class GDPRService {
  private static instance: GDPRService;

  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): GDPRService {
    if (!GDPRService.instance) {
      GDPRService.instance = new GDPRService();
    }
    return GDPRService.instance;
  }

  // ==========================================
  // DATA SUBJECT RIGHTS REQUESTS
  // ==========================================

  async submitDataSubjectRequest(request: Omit<DataSubjectRequest, 'id' | 'created_at' | 'updated_at' | 'status'>): Promise<DataSubjectRequest> {
    try {
      const { data, error } = await supabase
        .from('data_subject_requests')
        .insert([{
          ...request,
          status: 'pending',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      // Notify DPO of the new request
      await this.notifyDPO({
        request_type: request.request_type,
        user_email: '', // Will be populated by the API
        user_id: request.user_id,
        request_details: { description: request.description },
        urgency_level: request.urgency_level,
        estimated_response_time: this.getEstimatedResponseTime(request.request_type)
      });

      return data;
    } catch (error) {
      console.error('Error submitting data subject request:', error);
      throw error;
    }
  }

  async getDataSubjectRequests(userId: string): Promise<DataSubjectRequest[]> {
    try {
      const { data, error } = await supabase
        .from('data_subject_requests')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching data subject requests:', error);
      throw error;
    }
  }

  async getDataSubjectRequest(requestId: string): Promise<DataSubjectRequest | null> {
    try {
      const { data, error } = await supabase
        .from('data_subject_requests')
        .select('*')
        .eq('id', requestId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching data subject request:', error);
      throw error;
    }
  }

  async updateDataSubjectRequestStatus(requestId: string, status: DataSubjectRequest['status'], notes?: string): Promise<void> {
    try {
      const updateData: any = {
        status,
        updated_at: new Date().toISOString()
      };

      if (notes) {
        updateData.resolution_notes = notes;
      }

      if (status === 'completed') {
        updateData.resolved_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('data_subject_requests')
        .update(updateData)
        .eq('id', requestId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating data subject request status:', error);
      throw error;
    }
  }

  // ==========================================
  // DATA EXPORT FUNCTIONALITY
  // ==========================================

  async exportUserData(): Promise<UserDataExport> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('User not authenticated');

      const response = await fetch('/api/export-user-data', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      const exportData = await response.json();
      return exportData;
    } catch (error) {
      console.error('Error exporting user data:', error);
      throw error;
    }
  }

  async downloadUserDataExport(): Promise<void> {
    try {
      const exportData = await this.exportUserData();
      
      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Log export action
      await this.logUserAction('data_export', 'gdpr_compliance', {
        export_date: new Date().toISOString(),
        export_format: 'json',
        total_records: exportData.export_metadata.total_records
      });

    } catch (error) {
      console.error('Error downloading user data export:', error);
      throw error;
    }
  }

  // ==========================================
  // CONSENT MANAGEMENT
  // ==========================================

  async getUserConsents(userId: string): Promise<ConsentRecord[]> {
    try {
      const { data, error } = await supabase
        .from('user_consents')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user consents:', error);
      throw error;
    }
  }

  async updateConsent(consentUpdate: Partial<ConsentRecord> & { user_id: string; consent_type: string }): Promise<ConsentRecord> {
    try {
      const { data, error } = await supabase
        .from('user_consents')
        .upsert([{
          ...consentUpdate,
          updated_at: new Date().toISOString()
        }], {
          onConflict: 'user_id,consent_type'
        })
        .select()
        .single();

      if (error) throw error;

      // Log consent change
      await this.logUserAction('consent_update', 'gdpr_compliance', {
        consent_type: consentUpdate.consent_type,
        consent_given: consentUpdate.consent_given,
        processing_purpose: consentUpdate.processing_purpose
      });

      return data;
    } catch (error) {
      console.error('Error updating consent:', error);
      throw error;
    }
  }

  async withdrawConsent(userId: string, consentType: string, withdrawalMethod: string = 'user_request'): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_consents')
        .update({
          consent_withdrawn: true,
          withdrawal_date: new Date().toISOString(),
          withdrawal_method: withdrawalMethod,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('consent_type', consentType);

      if (error) throw error;

      // Log consent withdrawal
      await this.logUserAction('consent_withdrawal', 'gdpr_compliance', {
        consent_type: consentType,
        withdrawal_method: withdrawalMethod,
        withdrawal_date: new Date().toISOString()
      });

    } catch (error) {
      console.error('Error withdrawing consent:', error);
      throw error;
    }
  }

  // ==========================================
  // DATA DELETION & CLEANUP
  // ==========================================

  async deleteUserData(userId: string, dataTypes: string[]): Promise<void> {
    try {
      const deletionTasks = [];

      if (dataTypes.includes('candidates')) {
        deletionTasks.push(
          supabase.from('candidates').delete().eq('user_id', userId)
        );
      }

      if (dataTypes.includes('jobs')) {
        deletionTasks.push(
          supabase.from('jobs').delete().eq('user_id', userId)
        );
      }

      if (dataTypes.includes('analysis')) {
        deletionTasks.push(
          supabase.from('analysis').delete().eq('user_id', userId)
        );
      }

      if (dataTypes.includes('interviews')) {
        deletionTasks.push(
          supabase.from('interviews').delete().eq('user_id', userId)
        );
      }

      if (dataTypes.includes('projects')) {
        deletionTasks.push(
          supabase.from('projects').delete().eq('user_id', userId)
        );
      }

      // Execute all deletion tasks
      await Promise.all(deletionTasks);

      // Log data deletion
      await this.logUserAction('data_deletion', 'gdpr_compliance', {
        deleted_data_types: dataTypes,
        deletion_date: new Date().toISOString(),
        deletion_method: 'user_request'
      });

    } catch (error) {
      console.error('Error deleting user data:', error);
      throw error;
    }
  }

  async anonymizeUserData(userId: string): Promise<void> {
    try {
      // Anonymize personal data while preserving analytics
      const anonymizedData = {
        full_name: '[ANONYMIZED]',
        email: '[ANONYMIZED]',
        company: '[ANONYMIZED]',
        anonymized_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('profiles')
        .update(anonymizedData)
        .eq('user_id', userId);

      if (error) throw error;

      // Log anonymization
      await this.logUserAction('data_anonymization', 'gdpr_compliance', {
        anonymization_date: new Date().toISOString(),
        anonymization_method: 'user_request'
      });

    } catch (error) {
      console.error('Error anonymizing user data:', error);
      throw error;
    }
  }

  // ==========================================
  // PRIVATE HELPER METHODS
  // ==========================================

  private async notifyDPO(notification: {
    request_type: string;
    user_email: string;
    user_id: string;
    request_details: any;
    urgency_level: 'low' | 'medium' | 'high' | 'critical';
    estimated_response_time: number;
  }): Promise<void> {
    try {
      const response = await fetch('/api/notify-dpo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notification)
      });

      if (!response.ok) {
        throw new Error(`DPO notification failed: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error notifying DPO:', error);
      // Don't throw here - DPO notification failure shouldn't block the request
    }
  }

  private getEstimatedResponseTime(requestType: string): number {
    // Response times in hours
    const responseTimes: Record<string, number> = {
      'data_access': 72, // 3 days
      'data_rectification': 48, // 2 days
      'data_erasure': 168, // 7 days (complex)
      'data_portability': 168, // 7 days (complex)
      'processing_restriction': 48, // 2 days
      'consent_withdrawal': 24, // 1 day
      'objection_processing': 72, // 3 days
      'automated_decision_objection': 72 // 3 days
    };

    return responseTimes[requestType] || 72; // Default 3 days
  }

  private async logUserAction(action: string, feature: string, metadata: any): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('user_actions')
        .insert({
          user_id: user.id,
          action_type: action,
          feature_name: feature,
          metadata,
          timestamp: new Date().toISOString()
        });
    } catch (error) {
      console.error('Error logging user action:', error);
      // Don't throw - logging failure shouldn't block the main operation
    }
  }

  // ==========================================
  // COMPLIANCE UTILITIES
  // ==========================================

  async getDataProcessingRecord(userId: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('data_processing_records')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching data processing record:', error);
      throw error;
    }
  }

  async getGDPRAuditLog(userId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('gdpr_audit_log')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching GDPR audit log:', error);
      throw error;
    }
  }

  async validateDataRetention(): Promise<{
    expired_records: number;
    retention_policies: any[];
    cleanup_required: boolean;
  }> {
    try {
      const { data, error } = await supabase
        .from('data_retention_policies')
        .select('*')
        .eq('policy_active', true);

      if (error) throw error;

      // This would be expanded with actual retention validation logic
      return {
        expired_records: 0,
        retention_policies: data || [],
        cleanup_required: false
      };
    } catch (error) {
      console.error('Error validating data retention:', error);
      throw error;
    }
  }

  // ==========================================
  // CONSENT MANAGEMENT FOR COMPONENT
  // ==========================================

  /**
   * Get user consents for ConsentManagement component
   */
  async getUserConsentsForComponent(userId: string): Promise<UserConsent[]> {
    try {
      const { data, error } = await supabase
        .from('user_consents')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch user consents: ${error.message}`);
      }

      return (data || []).map(consent => ({
        purpose_id: consent.purpose_id,
        granted: consent.granted,
        granted_at: consent.granted_at,
        withdrawn_at: consent.withdrawn_at,
        consent_version: consent.consent_version,
        ip_address: consent.ip_address,
        user_agent: consent.user_agent
      }));
    } catch (error) {
      console.error('Error fetching user consents:', error);
      throw error;
    }
  }

  /**
   * Update user consent for a specific purpose
   */
  async updateUserConsent(userId: string, purposeId: string, granted: boolean): Promise<void> {
    try {
      const timestamp = new Date().toISOString();
      const userAgent = navigator.userAgent;
      
      // First, check if consent record exists
      const { data: existing, error: fetchError } = await supabase
        .from('user_consents')
        .select('*')
        .eq('user_id', userId)
        .eq('purpose_id', purposeId)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw new Error(`Failed to check existing consent: ${fetchError.message}`);
      }

      const consentData = {
        user_id: userId,
        purpose_id: purposeId,
        granted,
        granted_at: granted ? timestamp : existing?.granted_at,
        withdrawn_at: granted ? null : timestamp,
        consent_version: '1.0',
        ip_address: null, // Could be populated from server if needed
        user_agent: userAgent,
        updated_at: timestamp
      };

      let result;
      if (existing) {
        // Update existing consent
        result = await supabase
          .from('user_consents')
          .update(consentData)
          .eq('user_id', userId)
          .eq('purpose_id', purposeId);
      } else {
        // Insert new consent
        result = await supabase
          .from('user_consents')
          .insert({
            ...consentData,
            created_at: timestamp
          });
      }

      if (result.error) {
        throw new Error(`Failed to update consent: ${result.error.message}`);
      }

      // Log the consent change for audit purposes
      await this.logGDPRAuditEvent(userId, 'consent_updated', {
        purpose_id: purposeId,
        granted,
        timestamp,
        user_agent: userAgent
      });
    } catch (error) {
      console.error('Error updating user consent:', error);
      throw error;
    }
  }

  /**
   * Log GDPR audit event
   */
  private async logGDPRAuditEvent(userId: string, eventType: string, metadata: any): Promise<void> {
    try {
      const { error } = await supabase
        .from('gdpr_audit_log')
        .insert([{
          user_id: userId,
          event_type: eventType,
          event_data: metadata,
          timestamp: new Date().toISOString(),
          ip_address: null, // Could be populated from server
          user_agent: navigator.userAgent
        }]);

      if (error) {
        console.error('Error logging GDPR audit event:', error);
      }
    } catch (error) {
      console.error('Error logging GDPR audit event:', error);
    }
  }
}

// Interface for ConsentManagement component
export interface UserConsent {
  purpose_id: string;
  granted: boolean;
  granted_at: string;
  withdrawn_at?: string;
  consent_version: string;
  ip_address?: string;
  user_agent?: string;
}

// Export singleton instance
export const gdprService = GDPRService.getInstance(); 